import { useCallback, useContext, useEffect, useState } from "react";
import { GetTrackResponse, TrackState } from "../../proto/api_pb";
import { GetTrackVersionsResponse } from "../../proto/api_pb";
import { RpcError } from "grpc-web";
import { useParams } from "react-router-dom";
import { TrackObj, TrackVersionsObj } from "../MyTracks/types";
import { fetchTrack, postTrack } from "../../services/trackService";
import { fetchTrackVersions } from "../../services/trackService";
import TrackComponent from "../../components/TrackComponents/TrackComponent";
import TrackAddModal from "../../components/Modal/TrackAddModal";
import CollabModal from "../../components/Modal/CollabModal";
import { SingleTrackContainer } from "../../components/TrackVersion/styles";
import AddVersionModal from "../../components/Modal/AddVersionModal";
import AddTrackFileModal from "../../components/Modal/AddTrackFileModal";
import Mixpanel from "../../utils/mixpanel";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { TrackDeleteConfirmModal } from "../../components/Modal/TrackDeleteConfirmModal";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { getPaymentMethod } from "../../components/TrackComponents/TrackHandler/trackHandlers";
import { AuthContext } from "../../contexts";

export const SingleTrack: React.FC<{
  userVerified: boolean;
  singleTrackPage: boolean;
}> = ({ userVerified, singleTrackPage }) => {
  const [evokeCallBack, setEvokeCallBack] = useState(0);
  const [trackVersions, setTrackVersions] = useState<TrackVersionsObj[]>([]);
  const [track, setTrack] = useState<TrackObj>();
  const [open, setOpen] = useState<boolean>(false);
  const [state, setState] = useState<string>("Uploading track...");
  const [openAddVersionModal, setOpenAddVersionModal] = useState(false);
  const [openAddTrackFileModal, setOpenAddTrackFileModal] = useState(false);
  const [previewState, setPreviewState] =
    useState<string>("Uploading track...");
  const { getValidAccessToken } = useGetValidAccessToken();
  const { id } = useParams();
  const [openCollabsModal, setOpenCollabsModal] = useState(false);
  const [playingTime, setPlayingTime] = useState<any>(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [intervalTimeout, setIntervalTimeOut] = useState<any>();
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [isFileAdded, setIsFileAdded] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean>(false);
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "OmMuse - Track";
  }, []);
  const stopAllVersionSongs = () => {
    setTrackVersions(trackVersions.map((n) => ({ ...n, isPlaying: false })));
  };
  const handleMainPlay = () => {
    if (track) {
      const song = track?.waveSurfer;
      if (song) {
        song.playPause();
      }
      if (song?.isPlaying()) {
        setTrack({ ...track, isPlaying: true });
        stopAllVersionSongs();
        const timeOut: any = setInterval(() => {
          if (track.waveSurfer) {
            const seekPosition = track.waveSurfer.getCurrentTime();
            setPlayingTime(seekPosition);
          }
          setIntervalTimeOut(timeOut);
        }, 1000);
      } else {
        setTrack({ ...track, isPlaying: false });
        clearInterval(intervalTimeout);
      }
    }
  };

  const handleMainStop = () => {
    if (track) {
      const song = track?.waveSurfer;
      if (song) {
        song.stop();
      }
      if (!song?.isPlaying()) {
        setTrack({ ...track, isPlaying: false });
      }
    }
  };

  const setMainAudioTrack = (_id: string, waveSurfer: WaveSurfer) => {
    if (track) {
      setTrack({ ...track, waveSurfer });
      const audio = new Audio(track.trackUrl);
      audio &&
        audio.addEventListener("loadedmetadata", () => {
          const duration = audio.duration;
          setAudioDuration(duration);
        });
    }
  };
  const handleFetch = useCallback(async () => {
    const token = await getValidAccessToken();
    try {
      const response = (await fetchTrack(token, id!)) as GetTrackResponse;
      setTrack({
        track: response.getTrack(),
        trackUrl: response.getTrackVersion()!.getTrackUrl(),
        isPlaying: false,
        waveSurfer: undefined,
      });
    } catch (e) {
      const error = e as RpcError;
      console.log(error.code);
      console.log(error.message);
    }
  }, [getValidAccessToken, id]);

  useEffect(() => {
    handleFetch();
  }, [handleFetch]);

  useEffect(() => {
    const handleFetchTrackVersions = async () => {
      const token = await getValidAccessToken();
      try {
        const response = (await fetchTrackVersions(
          token,
          id!
        )) as GetTrackVersionsResponse;
        const newTrackVersionList = response
          .getTrackVersionsList()
          .map((el) => {
            return { track: el, isPlaying: false };
          });
        setTrackVersions(newTrackVersionList);
      } catch (e) {
        const error = e as RpcError;
        console.log(error.code);
        console.log(error.message);
      }
    };
    handleFetchTrackVersions();
  }, [getValidAccessToken, id]);

  const handleOpen = () => {
    setEvokeCallBack((prev) => prev + 1);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseAddVersionModal = () => {
    setOpenAddVersionModal(false);
  };

  const handleCloseTrackFileModal = () => {
    setOpenAddTrackFileModal(false);
  };

  const handleCloseCollabsModal = () => {
    setOpenCollabsModal(false);
  };
  const handleOpenCollabsModal = () => {
    setEvokeCallBack((prev) => prev + 1);
    if (track?.waveSurfer?.isPlaying()) {
      track?.waveSurfer?.stop();
    }
    setOpenCollabsModal(true);
  };

  const handleState = (state: string) => {
    setState(`${state}`);
  };

  const handlePreviewState = (state: string) => {
    setPreviewState(`${state}`);
  };

  const openDeleteConfirmModal = (id: string) => {
    Mixpanel.track("delete_track_open");
    setDeleteConfirmModalOpen(true);
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModalOpen(false);
  };

  const handleDeleteTrack = async () => {
    if (track?.track) {
      track.track.setState(TrackState.TRACK_STATE_DELETED);
      const postTrackResponse = await postTrack(
        track.track,
        await getValidAccessToken()
      );
      if (postTrackResponse instanceof RpcError) {
        alert("Track was not deleted successfully");
        throw postTrackResponse;
      }
      toast.success("Track has been successfully removed");
    }
    closeDeleteConfirmModal();
    navigate("/my-tracks");
  };

  const handleOpenVersionModal = () => {
    setEvokeCallBack((prev) => prev + 1);
    setOpenAddVersionModal(true);
  };
  const handleOpenTrackFileModal = () => {
    setEvokeCallBack((prev) => prev + 1);
    setOpenAddTrackFileModal(true);
  };

  const handleUseEffect = useCallback(async () => {
    const token = await getValidAccessToken();
    const getMethod = await getPaymentMethod(token, userId);
    if (getMethod) setHasPaymentMethod(getMethod.getHasPaymentMethod());
  }, [getValidAccessToken, userId]);

  useEffect(() => {
    handleUseEffect();
  }, [handleUseEffect]);

  return (
    <>
      <TrackDeleteConfirmModal
        deleteConfirmModalOpen={deleteConfirmModalOpen}
        closeDeleteConfirmModal={closeDeleteConfirmModal}
        handleDeleteTrack={handleDeleteTrack}
      />
      {open && (
        <TrackAddModal
          open={open}
          handleClose={handleClose}
          trackToEdit={track?.track}
          trackToEditUrl={track?.trackUrl}
        />
      )}
      {openAddVersionModal && (
        <AddVersionModal
          open={openAddVersionModal}
          handleClose={handleCloseAddVersionModal}
          track={track?.track}
        />
      )}
      {openAddTrackFileModal && (
        <AddTrackFileModal
          open={openAddTrackFileModal}
          handleClose={handleCloseTrackFileModal}
          track={track?.track}
          handleFileAdded={() => setIsFileAdded((prev) => !prev)}
        />
      )}
      <SingleTrackContainer>
        {track && (
          <TrackComponent
            item={track}
            setAudioTrack={setMainAudioTrack}
            handlePlay={handleMainPlay}
            handleStop={handleMainStop}
            handleOpen={handleOpen}
            displayForTrackDetails={true}
            versionId={track.track?.getTrackVersionId()}
            handleState={handleState}
            state={state}
            handlePreviewState={handlePreviewState}
            previewState={previewState}
            expand={true}
            handleOpenVersionModal={handleOpenVersionModal}
            handleOpenTrackFileModal={handleOpenTrackFileModal}
            handleFetch={handleFetch}
            userVerified={userVerified}
            handleOpenCollabsModal={handleOpenCollabsModal}
            playingTime={playingTime}
            setPlayingTime={setPlayingTime}
            audioDuration={audioDuration}
            openDeleteConfirmModal={openDeleteConfirmModal}
            singleTrackPage={singleTrackPage}
            isFileAdded={isFileAdded}
            isTrackConnectedToTutorial={false}
            evokeCallBack={evokeCallBack}
            setEvokeCallBack={setEvokeCallBack}
            hasPaymentMethod={hasPaymentMethod}
          />
        )}
      </SingleTrackContainer>
      {openCollabsModal && (
        <CollabModal
          openCollabsModal={openCollabsModal}
          handleCloseCollabsModal={handleCloseCollabsModal}
          trackToEdit={track?.track}
        />
      )}
    </>
  );
};
